import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import { DELETE, EDIT } from "const"
import { hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_DRIVE,
    StoreType,
    DriveActionType,

    isRecycleOpened,
    setDrivePopupMenuShow,
    setDriveAction,

    DRIVE_ACTION_RENAME,
    DRIVE_ACTION_MOVE,
    DRIVE_ACTION_RESTORE,
    DRIVE_ACTION_DELETE,
    isProjectFolderOpened,
    closeModal,
} from "client/store"
import {
    PopupMenu,
    PopupMenuItem,
} from "client/components"

import { OpenFolder, OpenFile } from "../actions/open"

const Menu = () => {
    const drivePath = useSelector((state: StoreType) => state.drive.drivePath)
    const baseRoute = useSelector((state: StoreType) => state.drive.baseRoute)
    const onSelectFile = useSelector((state: StoreType) => state.drive.onSelectFile)
    const onSelectFiles = useSelector((state: StoreType) => state.drive.onSelectFiles)
    const onSelectVersion = useSelector((state: StoreType) => state.drive.onSelectVersion)
    const onSelect = onSelectFile || onSelectFiles || onSelectVersion

    const folderActive = useSelector((state: StoreType) => state.drive.folder.active)
    const fileActive = useSelector((state: StoreType) => state.drive.file.active)

    const menuShow = useSelector((state: StoreType) => state.drive.menuShow)
    const menuTarget = useSelector((state: StoreType) => state.drive.menuTarget) as HTMLElement
    const menuPlacement = useSelector((state: StoreType) => state.drive.menuPlacement)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [menuItems, setMenuItems] = useState<any[]>([])

    const recycleOpened = isRecycleOpened(drivePath)
    const projectId = isProjectFolderOpened(drivePath)

    const accountEditAccess = hasAccess(PRM_DRIVE, EDIT)
    const projectEditAccess = hasProjectAccess(PRM_DRIVE, EDIT, projectId)
    const editAccess = !projectId
        ? accountEditAccess
        : projectEditAccess

    const accountDeleteAccess = hasAccess(PRM_DRIVE, DELETE)
    const projectDeleteAccess = hasProjectAccess(PRM_DRIVE, DELETE, projectId)
    const deleteAccess = !projectId
        ? accountDeleteAccess
        : projectDeleteAccess


    const hideMenu = () => {
        dispatch(setDrivePopupMenuShow(false))
    }

    useEffect(() => {
        document.addEventListener("click", hideMenu)
        return () => document.removeEventListener("click", hideMenu)
    }, [])

    useEffect(() => {
        if (!folderActive && !fileActive) setMenuItems([])
        else {
            if (!recycleOpened) setMenuItems(createMenu())
            if (recycleOpened) setMenuItems(createRecycleMenu())
        }
    }, [folderActive, fileActive])

    const menuClick = (e: React.MouseEvent, action: DriveActionType) => {
        e.stopPropagation()
        dispatch(setDrivePopupMenuShow(false))
        dispatch(setDriveAction(action))
    }

    const openFolderItem = (
        <PopupMenuItem
            key="openFolder"
            onClick={() => OpenFolder(folderActive, userData, onSelect, dispatch)}
        >
            {lang.open}
        </PopupMenuItem>
    )
    const openFileItem = (
        <PopupMenuItem
            key="openFile"
            onClick={() => OpenFile(fileActive.id, fileActive.url, drivePath, baseRoute, onSelect, dispatch)}
        >
            {lang.details}
        </PopupMenuItem>
    )
    const renameItem = (
        <PopupMenuItem key="rename" onClick={(e) => menuClick(e, DRIVE_ACTION_RENAME)}>
            {lang.rename}
        </PopupMenuItem>
    )
    const moveItem = (
        <PopupMenuItem key="move" onClick={(e) => menuClick(e, DRIVE_ACTION_MOVE)}>
            {lang.move}
        </PopupMenuItem>
    )
    const restoreItem = (
        <PopupMenuItem key="restore" onClick={(e) => menuClick(e, DRIVE_ACTION_RESTORE)}>
            {lang.restore}
        </PopupMenuItem>
    )
    const deleteItem = (
        <PopupMenuItem key="delete" onClick={(e) => menuClick(e, DRIVE_ACTION_DELETE)}>
            {lang.delete}
        </PopupMenuItem>
    )
    const selectItem = (
        <PopupMenuItem
            key="select"
            onClick={() => {
                if (onSelectFile) onSelectFile(fileActive)
                if (onSelectVersion) onSelectVersion(fileActive.versions && fileActive.versions[0], fileActive)
                dispatch(closeModal()) // close drive list modal
            }}
        >
            {lang.select}
        </PopupMenuItem>
    )

    const createMenu = () => {
        const menu: any[] = []

        // project and QID product folders can be only opened
        if (folderActive && (folderActive.project || folderActive.product)) {
            menu.push(openFolderItem)
        } else {
            if (fileActive && (onSelectFile || onSelectVersion)) menu.push(selectItem)

            if (folderActive) menu.push(openFolderItem)
            if (fileActive) menu.push(openFileItem)

            if (editAccess) menu.push(renameItem, moveItem)
            if (deleteAccess) menu.push(deleteItem)
        }

        return menu
    }

    const createRecycleMenu = () => {
        const menu: any[] = []

        if (folderActive) menu.push(openFolderItem)
        if (fileActive) menu.push(openFileItem)

        if (editAccess) menu.push(restoreItem)
        if (deleteAccess) menu.push(deleteItem)

        return menu
    }

    if (!menuItems.length) return null
    return (
        <PopupMenu
            show={menuShow}
            target={menuTarget}
            placement={menuPlacement}
        >
            {menuItems}
        </PopupMenu>
    )
}

export default Menu
